import { useState } from 'react';
import { format } from 'date-fns';
import { publicBookingApi } from '../../api/publicBooking';
import { ApiError } from '../../api/client';
import {
	ServiceMultiSelect,
	BookingDateTime,
	BookingProgress,
	BookingCostBreakdown,
	BookingSummaryRow,
} from '../../components/booking';
import { usePublicBookingSelection } from '../../hooks/usePublicBookingSelection';
import { buildAppointmentDateTime } from '../../utils/bookingFormat';
import { formatSize, formatLength } from '../../constants/serviceOptions';
import type {
	PublicBookingInfo,
	PublicBookingSlot,
	CreatePublicBookingResponse,
} from '../../types/publicBooking';

const STEPS = ['Your Info', 'Service', 'Date & Time', 'Review'];

interface WalkInCheckInProps {
	token: string;
	info: PublicBookingInfo;
}

interface ClientForm {
	firstName: string;
	lastName: string;
	phone: string;
	email: string;
}

const EMPTY_CLIENT: ClientForm = { firstName: '', lastName: '', phone: '', email: '' };

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Walk-in check-in — the no-payment booking link.
 *
 * Four steps then a confirmation: the customer identifies themselves, picks
 * services (with size and length where those price them), states a preferred
 * time, and confirms. No
 * deposit is taken, so confirming creates the appointment outright.
 */
const WalkInCheckIn = ({ token, info }: WalkInCheckInProps) => {
	const [currentStep, setCurrentStep] = useState(1);
	const [client, setClient] = useState<ClientForm>(EMPTY_CLIENT);
	const [fieldErrors, setFieldErrors] = useState<Partial<Record<keyof ClientForm, string>>>({});
	const [stepError, setStepError] = useState<string | null>(null);
	const [notes, setNotes] = useState('');
	const [selectedDate, setSelectedDate] = useState<Date | null>(null);
	const [selectedSlot, setSelectedSlot] = useState<PublicBookingSlot | null>(null);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [confirmation, setConfirmation] = useState<CreatePublicBookingResponse | null>(null);

	const selection = usePublicBookingSelection(info.services);

	const updateClient = (field: keyof ClientForm, value: string) => {
		setClient(prev => ({ ...prev, [field]: value }));
		setFieldErrors(prev => ({ ...prev, [field]: undefined }));
	};

	const goToStep = (step: number) => {
		setStepError(null);
		setCurrentStep(step);
		window.scrollTo({ top: 0, behavior: 'smooth' });
	};

	// ── Per-step validation ──
	const validateClient = (): boolean => {
		const errors: Partial<Record<keyof ClientForm, string>> = {};
		if (!client.firstName.trim()) errors.firstName = 'First name is required.';
		if (!client.lastName.trim()) errors.lastName = 'Last name is required.';
		if (!client.phone.trim()) errors.phone = 'Phone number is required.';
		if (!client.email.trim()) errors.email = 'Email address is required.';
		else if (!EMAIL_PATTERN.test(client.email.trim())) errors.email = 'Enter a valid email.';
		setFieldErrors(errors);
		return Object.keys(errors).length === 0;
	};

	const handleContinueFromInfo = () => {
		if (!validateClient()) return;
		goToStep(2);
	};

	const handleContinueFromService = () => {
		if (selection.selectedServices.length === 0) {
			setStepError('Please select at least one service.');
			return;
		}
		// Only asked for when the chosen services are priced by them — and then
		// answered, so the cost shown is the one the salon will charge.
		if (selection.requiresSize && !selection.size) {
			setStepError('Please select a size.');
			return;
		}
		if (selection.requiresLength && !selection.length) {
			setStepError('Please select a length.');
			return;
		}
		goToStep(3);
	};

	const handleContinueFromDateTime = () => {
		if (!selectedDate) {
			setStepError('Please select a preferred date.');
			return;
		}
		if (!selectedSlot) {
			setStepError('Please select a preferred time slot.');
			return;
		}
		goToStep(4);
	};

	const handleConfirm = async () => {
		if (!selectedDate || !selectedSlot) return;
		setIsSubmitting(true);
		setStepError(null);
		try {
			const response = await publicBookingApi.create(token, {
				firstName: client.firstName.trim(),
				lastName: client.lastName.trim(),
				phone: client.phone.trim(),
				email: client.email.trim(),
				serviceIds: selection.selectedIds,
				...(selection.size && { size: selection.size }),
				...(selection.length && { length: selection.length }),
				notes: notes.trim(),
				appointmentDateTime: buildAppointmentDateTime(selectedDate, selectedSlot),
			});
			setConfirmation(response);
			window.scrollTo({ top: 0, behavior: 'smooth' });
		} catch (error) {
			const detail =
				error instanceof ApiError
					? error.getFieldErrorMessages() || error.message
					: 'Please try again.';
			setStepError(`We could not complete your check-in. ${detail}`);
		} finally {
			setIsSubmitting(false);
		}
	};

	const serviceNames = selection.selectedServices.map(s => s.name).join(', ');
	const dateLabel = selectedDate ? format(selectedDate, 'EEEE, MMMM d, yyyy') : '';
	const timeLabel = selectedSlot ? selectedSlot.label || selectedSlot.time : '';

	return (
		<div className="pb pb--walkin">
			{/* Topbar */}
			<div className="pb__topbar">
				<div className="pb__topbar-inner">
					<div className="pb__brand">
						<div className="pb__brand-logo">
							<svg
								width="22"
								height="22"
								viewBox="0 0 24 24"
								fill="none"
								stroke="currentColor"
								strokeWidth="2"
								strokeLinecap="round"
								strokeLinejoin="round"
							>
								<path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
								<polyline points="9 22 9 12 15 12 15 22" />
							</svg>
						</div>
						<div>
							<div className="pb__brand-name">{info.providerName}</div>
							{info.locationName && (
								<div className="pb__brand-tagline">{info.locationName}</div>
							)}
						</div>
					</div>
					<div className="pb__badge">🚶 Walk-In</div>
				</div>
			</div>

			{/* Hero */}
			<div className="pb__hero">
				<div className="pb__hero-eyebrow">No Appointment Needed</div>
				<div className="pb__hero-title">Walk-In Check-In</div>
				<div className="pb__hero-sub">
					Walk-in is not prioritized and is subject to stylist availability
				</div>
			</div>

			<div className="pb__content">
				<div className="pb__content-inner">
					{!confirmation && <BookingProgress steps={STEPS} currentStep={currentStep} />}

					{/* ── Step 1: Your Info ── */}
					{!confirmation && currentStep === 1 && (
						<div className="pb__card">
							<div className="pb__card-header">
								<div className="pb__card-title">Your Information</div>
								<div className="pb__card-desc">
									Enter your details so we can check you in quickly
								</div>
							</div>

							<div className="pb__alert">
								<span className="pb__alert-icon">🚶</span>
								<div className="pb__alert-body">
									<strong>Walk-In — No deposit required</strong>
									Walk-in is not prioritized and is subject to stylist
									availability
								</div>
							</div>

							<div className="pb__actions pb__actions--top">
								<div />
								<button
									type="button"
									className="pb__btn pb__btn--primary"
									onClick={handleContinueFromInfo}
								>
									Continue →
								</button>
							</div>

							<div className="pb__form-grid">
								<TextField
									label="First Name"
									required
									value={client.firstName}
									onChange={value => updateClient('firstName', value)}
									placeholder="e.g. Kenya"
									error={fieldErrors.firstName}
								/>
								<TextField
									label="Last Name"
									required
									value={client.lastName}
									onChange={value => updateClient('lastName', value)}
									placeholder="e.g. Bennett"
									error={fieldErrors.lastName}
								/>
								<TextField
									label="Phone Number"
									required
									type="tel"
									value={client.phone}
									onChange={value => updateClient('phone', value)}
									placeholder="(718) 555-0000"
									error={fieldErrors.phone}
								/>
								<TextField
									label="Email Address"
									required
									type="email"
									value={client.email}
									onChange={value => updateClient('email', value)}
									placeholder="you@email.com"
									error={fieldErrors.email}
								/>
							</div>

							<div className="pb__actions">
								<div />
								<button
									type="button"
									className="pb__btn pb__btn--primary"
									onClick={handleContinueFromInfo}
								>
									Continue →
								</button>
							</div>
						</div>
					)}

					{/* ── Step 2: Service ── */}
					{!confirmation && currentStep === 2 && (
						<div className="pb__card">
							<div className="pb__card-header">
								<div className="pb__card-title">Service Details</div>
								<div className="pb__card-desc">
									Choose your service, size, and length
								</div>
							</div>

							{stepError && <ErrorBanner message={stepError} />}

							<div className="pb__actions pb__actions--top">
								<button
									type="button"
									className="pb__btn pb__btn--secondary"
									onClick={() => goToStep(1)}
								>
									← Back
								</button>
								<button
									type="button"
									className="pb__btn pb__btn--primary"
									onClick={handleContinueFromService}
								>
									Continue →
								</button>
							</div>

							<div className="pb__section-label">
								Select Service
								<span className="pb__section-hint">(select all that apply)</span>
							</div>

							<ServiceMultiSelect
								services={info.services}
								selectedIds={selection.selectedIds}
								onToggle={selection.toggleService}
								onRemove={selection.removeService}
								categories={selection.categories}
								activeCategory={selection.activeCategory}
								onCategoryChange={selection.setActiveCategory}
							/>

							{/* Hidden outright for services priced by neither field. */}
							{selection.sizeApplies && (
								<>
									<div className="pb__divider" />
									<div className="pb__section-label">Select Size</div>
									{selection.hasNoSharedSize ? (
										<div className="pb__empty-hint">
											The selected services have no size in common — your
											stylist will confirm this with you.
										</div>
									) : (
										<div className="pb__pill-group">
											{selection.sizeOptions.map(option => (
												<button
													key={option}
													type="button"
													className={`pb__pill${selection.size === option ? ' is-selected' : ''}`}
													onClick={() => selection.setSize(option)}
												>
													{formatSize(option)}
												</button>
											))}
										</div>
									)}
								</>
							)}

							{selection.lengthApplies && (
								<>
									<div className="pb__divider" />
									<div className="pb__section-label">Select Length</div>
									{selection.hasNoSharedLength ? (
										<div className="pb__empty-hint">
											{selection.size
												? 'The selected services share no length at this size. Try another size, or continue and your stylist will confirm.'
												: 'The selected services have no length in common — your stylist will confirm this with you.'}
										</div>
									) : (
										<div className="pb__pill-group">
											{selection.lengthOptions.map(option => (
												<button
													key={option}
													type="button"
													className={`pb__pill${selection.length === option ? ' is-selected' : ''}`}
													onClick={() => selection.setLength(option)}
												>
													{formatLength(option)}
												</button>
											))}
										</div>
									)}
								</>
							)}

							{selection.costLines.length > 0 && (
								<BookingCostBreakdown
									lines={selection.costLines}
									total={selection.serviceTotal}
								/>
							)}

							<div className="pb__divider" />
							<div>
								<label className="pb__label" htmlFor="walkin-notes">
									Additional Notes
								</label>
								<textarea
									id="walkin-notes"
									className="pb__textarea"
									value={notes}
									onChange={event => setNotes(event.target.value)}
									placeholder="Any special requests or style details..."
								/>
							</div>

							<div className="pb__actions">
								<button
									type="button"
									className="pb__btn pb__btn--secondary"
									onClick={() => goToStep(1)}
								>
									← Back
								</button>
								<button
									type="button"
									className="pb__btn pb__btn--primary"
									onClick={handleContinueFromService}
								>
									Continue →
								</button>
							</div>
						</div>
					)}

					{/* ── Step 3: Date & Time ── */}
					{!confirmation && currentStep === 3 && (
						<div className="pb__card">
							<div className="pb__card-header">
								<div className="pb__card-title">Preferred Date &amp; Time</div>
								<div className="pb__card-desc">
									Let us know your preferred time — walk-ins are served in order
									of arrival
								</div>
							</div>

							{stepError && <ErrorBanner message={stepError} />}

							<div className="pb__actions pb__actions--top">
								<button
									type="button"
									className="pb__btn pb__btn--secondary"
									onClick={() => goToStep(2)}
								>
									← Back
								</button>
								<button
									type="button"
									className="pb__btn pb__btn--primary"
									onClick={handleContinueFromDateTime}
								>
									Review Check-In →
								</button>
							</div>

							<BookingDateTime
								token={token}
								durationMinutes={selection.totalDurationMinutes}
								selectedDate={selectedDate}
								selectedSlot={selectedSlot}
								onDateChange={setSelectedDate}
								onSlotChange={setSelectedSlot}
							/>

							<div className="pb__actions">
								<button
									type="button"
									className="pb__btn pb__btn--secondary"
									onClick={() => goToStep(2)}
								>
									← Back
								</button>
								<button
									type="button"
									className="pb__btn pb__btn--primary"
									onClick={handleContinueFromDateTime}
								>
									Review Check-In →
								</button>
							</div>
						</div>
					)}

					{/* ── Step 4: Review ── */}
					{!confirmation && currentStep === 4 && (
						<div className="pb__card">
							<div className="pb__card-header">
								<div className="pb__card-title">Review &amp; Confirm</div>
								<div className="pb__card-desc">
									Double-check your walk-in details before confirming
								</div>
							</div>

							{stepError && <ErrorBanner message={stepError} />}

							<div className="pb__actions pb__actions--top">
								<button
									type="button"
									className="pb__btn pb__btn--secondary"
									onClick={() => goToStep(3)}
									disabled={isSubmitting}
								>
									← Back
								</button>
								<button
									type="button"
									className="pb__btn pb__btn--success"
									onClick={handleConfirm}
									disabled={isSubmitting}
								>
									{isSubmitting ? 'Checking you in…' : '✓ Confirm Walk-In'}
								</button>
							</div>

							<div className="pb__section-label">Client</div>
							<div className="pb__summary">
								<BookingSummaryRow
									label="Name"
									value={`${client.firstName} ${client.lastName}`}
								/>
								<BookingSummaryRow label="Phone" value={client.phone} />
								<BookingSummaryRow label="Email" value={client.email} />
							</div>

							<div className="pb__divider" />
							<div className="pb__section-label">Service</div>
							<div className="pb__summary">
								<BookingSummaryRow label="Services" value={serviceNames} />
								{selection.size && (
									<BookingSummaryRow
										label="Size"
										value={formatSize(selection.size)}
									/>
								)}
								{selection.length && (
									<BookingSummaryRow
										label="Length"
										value={formatLength(selection.length)}
									/>
								)}
								{notes.trim() && (
									<BookingSummaryRow
										label="Notes"
										value={notes.trim()}
										variant="normal"
									/>
								)}
							</div>

							<div className="pb__divider" />
							<div className="pb__section-label">Preferred Time</div>
							<div className="pb__summary">
								<BookingSummaryRow label="Date" value={dateLabel} />
								<BookingSummaryRow label="Time" value={timeLabel} />
							</div>

							<div className="pb__divider" />
							<div className="pb__section-label">Service Cost</div>
							<BookingCostBreakdown
								lines={selection.costLines}
								total={selection.serviceTotal}
							/>

							<div className="pb__notice">
								<span className="pb__alert-icon">ℹ️</span>
								<div>
									<strong>No Deposit Required</strong>
									Walk-in check-ins do not require a deposit or payment. You will
									be served in order of arrival. The salon reserves the right to
									adjust wait times based on capacity.
								</div>
							</div>

							<div className="pb__actions">
								<button
									type="button"
									className="pb__btn pb__btn--secondary"
									onClick={() => goToStep(3)}
									disabled={isSubmitting}
								>
									← Back
								</button>
								<button
									type="button"
									className="pb__btn pb__btn--success"
									onClick={handleConfirm}
									disabled={isSubmitting}
								>
									{isSubmitting ? 'Checking you in…' : '✓ Confirm Walk-In'}
								</button>
							</div>
						</div>
					)}

					{/* ── Confirmed ── */}
					{confirmation && (
						<div className="pb__card">
							<div className="pb__confirm-hero">
								<div className="pb__confirm-icon">✓</div>
								<div className="pb__confirm-title">You&apos;re Checked In!</div>
								<div className="pb__confirm-sub">
									Your walk-in has been registered. Please head to the salon —
									you&apos;ll be served in order of arrival.
								</div>
								<div className="pb__confirm-ref">
									Walk-In #{confirmation.reference}
								</div>
							</div>

							<div className="pb__divider" />
							<div className="pb__section-label">Check-In Summary</div>
							<div className="pb__summary">
								<BookingSummaryRow
									label="Reference"
									value={confirmation.reference}
								/>
								<BookingSummaryRow
									label="Name"
									value={`${client.firstName} ${client.lastName}`}
								/>
								<BookingSummaryRow label="Phone" value={client.phone} />
								<BookingSummaryRow label="Services" value={serviceNames} />
								{selection.size && (
									<BookingSummaryRow
										label="Size"
										value={formatSize(selection.size)}
									/>
								)}
								{selection.length && (
									<BookingSummaryRow
										label="Length"
										value={formatLength(selection.length)}
									/>
								)}
								<BookingSummaryRow label="Preferred Date" value={dateLabel} />
								<BookingSummaryRow label="Preferred Time" value={timeLabel} />
								<BookingSummaryRow
									label="Deposit"
									value="None — Walk-In"
									variant="positive"
								/>
							</div>

							<div className="pb__divider" />
							<div className="pb__section-label">Service Cost</div>
							<BookingCostBreakdown
								lines={selection.costLines}
								total={confirmation.serviceTotal || selection.serviceTotal}
							/>

							<div className="pb__notice">
								<span className="pb__alert-icon">💛</span>
								<div>
									<strong>Reminder</strong>
									No payment was collected. Walk-ins are welcome and served in the
									order they arrive. Thank you for choosing us!
								</div>
							</div>

							<div className="pb__actions pb__actions--center">
								<button
									type="button"
									className="pb__btn pb__btn--secondary"
									onClick={() => window.location.reload()}
								>
									+ New Walk-In
								</button>
							</div>
						</div>
					)}
				</div>
			</div>
		</div>
	);
};

// ── Local presentational helpers ────────────────────────────────

const TextField = ({
	label,
	value,
	onChange,
	placeholder,
	error,
	required,
	type = 'text',
}: {
	label: string;
	value: string;
	onChange: (value: string) => void;
	placeholder?: string;
	error?: string;
	required?: boolean;
	type?: string;
}) => {
	const id = `walkin-${label.toLowerCase().replace(/\s+/g, '-')}`;
	return (
		<div>
			<label className="pb__label" htmlFor={id}>
				{label}
				{required && <span className="pb__req">*</span>}
			</label>
			<input
				id={id}
				className={`pb__input${error ? ' has-error' : ''}`}
				type={type}
				value={value}
				onChange={event => onChange(event.target.value)}
				placeholder={placeholder}
			/>
			{error && <span className="pb__field-error">{error}</span>}
		</div>
	);
};

const ErrorBanner = ({ message }: { message: string }) => (
	<div className="pb__error-banner">
		<span>⚠️</span>
		<span>{message}</span>
	</div>
);

export default WalkInCheckIn;
